import { useState } from "react";
import DataPanel from "@/components/UI/DataPanel";
import CompactAlertCard from "./CompactAlertCard";
import ExpandedAlertCard from "./ExpandedAlertCard";
import type { AlertWithInsightProps } from "@/components/Insights/insights";
import type { SortDirection } from "@/components/Insights/insights.constants";
import { SORT_DIRECTIONS } from "@/components/Insights/insights.constants";
import { sortByDate } from "@/utils/sortByDate";

type AlertsPanelLayoutProps = {
  mineAlerts: AlertWithInsightProps[];
  sharedAlerts: AlertWithInsightProps[];
  compactMode: boolean;
};

export default function AlertsPanelLayout({
  mineAlerts,
  sharedAlerts,
  compactMode,
}: AlertsPanelLayoutProps) {
  const [mineSortDirection, setMineSortDirection] = useState<SortDirection>(
    SORT_DIRECTIONS.DESC,
  );
  const [sharedSortDirection, setSharedSortDirection] = useState<SortDirection>(
    SORT_DIRECTIONS.DESC,
  );

  const sortedMineAlerts = sortByDate(
    mineAlerts,
    (alert) => alert.updated_at,
    mineSortDirection,
  );
  const sortedSharedAlerts = sortByDate(
    sharedAlerts,
    (alert) => alert.updated_at,
    sharedSortDirection,
  );

  const showEmpty = mineAlerts.length === 0 && sharedAlerts.length === 0;

  if (showEmpty) {
    return (
      <div className="flex items-start justify-center px-2 py-4">
        <p className="text-[#00000073] text-base">No alerts available</p>
      </div>
    );
  }

  return (
    <div
      className={`flex ${compactMode ? "flex-col gap-4" : "flex-row gap-3"} py-4`}
    >
      <div className={compactMode ? "w-full" : "w-1/2"}>
        <DataPanel
          title="MINE"
          count={mineAlerts.length}
          sortDirection={mineSortDirection}
          onSortChange={setMineSortDirection}
          emptyMessage="No alerts have been created"
        >
          <div className="grid grid-cols-1 gap-3">
            {sortedMineAlerts.map((alert) =>
              compactMode ? (
                <CompactAlertCard key={alert.id} alert={alert} />
              ) : (
                <ExpandedAlertCard key={alert.id} alert={alert} />
              ),
            )}
          </div>
        </DataPanel>
      </div>
      <div className={compactMode ? "w-full" : "w-1/2"}>
        <DataPanel
          title="SHARED"
          count={sharedAlerts.length}
          sortDirection={sharedSortDirection}
          onSortChange={setSharedSortDirection}
          emptyMessage="No alerts have been shared"
        >
          <div className="grid grid-cols-1 gap-3">
            {sortedSharedAlerts.map((alert) =>
              compactMode ? (
                <CompactAlertCard key={alert.id} alert={alert} />
              ) : (
                <ExpandedAlertCard key={alert.id} alert={alert} />
              ),
            )}
          </div>
        </DataPanel>
      </div>
    </div>
  );
}
